<template>
  <div class="left-lnb" :class="{ collapsed: isLnbCollapsed }">
    <!-- 접기/펼치기 버튼 (상단 고정) -->
    <button class="lnb-collapse-btn" @click="toggleLnbCollapsed" :title="isLnbCollapsed ? '메뉴 펼치기' : '메뉴 접기'">
      <img
        :src="isLnbCollapsed ? '/images/icon-arrow-right.svg' : '/images/icon-arrow-left.svg'"
        :alt="isLnbCollapsed ? '펼치기' : '접기'"
      />
    </button>

    <!-- 상단: 로고 및 현재 메뉴 -->
    <div class="lnb-header" v-if="!isLnbCollapsed">
      <div class="lnb-logo-section">
        <div class="lnb-logo-icon">
          <img :src="currentMenuIcon" :alt="currentMenuName" />
        </div>
        <span class="lnb-current-menu">{{ currentMenuName }}</span>
      </div>
    </div>

    <!-- 접힌 상태일 때 메인 아이콘만 표시 -->
    <div v-if="isLnbCollapsed" class="lnb-collapsed-icon">
      <img :src="currentMenuIcon" :alt="currentMenuName" />
    </div>

    <!-- 접힌 상태일 때 구분선 -->
    <div v-if="isLnbCollapsed" class="lnb-collapsed-divider"></div>

    <!-- 구분선 -->
    <div class="lnb-divider" v-if="!isLnbCollapsed"></div>

    <!-- 메뉴 목록 -->
    <div class="lnb-menu-section" v-if="!isLnbCollapsed" ref="menuSectionRef">
      <ul class="lnb-menu-list">
        <li
          v-for="menu in currentLnbMenus"
          :key="menu.id"
          class="lnb-menu-item"
          :class="{ active: isSelectedSubMenu(menu.id) }"
          :ref="isSelectedSubMenu(menu.id) ? 'activeMenuRef' : undefined"
          @click="selectMenu(menu)"
        >
          <span class="lnb-menu-text">• {{ getTranslatedMenuName(menu) }}</span>
        </li>
      </ul>
    </div>
    <!-- 언어 선택 섹션 - 레벨2 메뉴 밑에 위치 -->
    <div class="lnb-language-section" v-if="!isLnbCollapsed">
      <section class="lang-header">
        <select v-model="locale">
          <option value="ko">한국어</option>
          <option value="en">English</option>
        </select>
      </section>
    </div>
    <!-- 하단: 사용자 정보 및 로그아웃 -->
    <div class="lnb-user-section">
      <div class="lnb-user-info">
        <div class="lnb-user-avatar">
          <img src="/images/icon-user.svg" alt="사용자" />
        </div>
        <div v-if="!isLnbCollapsed" class="lnb-user-details">
          <div class="lnb-user-name-row">
            <div class="lnb-user-name">{{ userName }}</div>
            <div class="lnb-user-type">{{ userRole }}</div>
          </div>
          <div class="lnb-user-location" @click="handleSiteClick" :class="{ clickable: siteList.length > 1 }">
            {{ selectedSiteName }}
            <span v-if="siteList.length > 1" class="site-change-hint"></span>
          </div>
        </div>
      </div>

      <button v-if="!isLnbCollapsed" class="lnb-logout-btn" @click="handleLogoutClick">
        <img src="/images/icon-logout.svg" alt="로그아웃" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch, nextTick } from "vue";
  import { useStore } from "@/store";
  import { useI18n } from "vue-i18n";
  import { useRouter } from "vue-router";
  import { handleLogout } from "@/utils/logoutHandler";
  import { popupQuestion } from "@/components/popup/Popups";
  import { getTranslatedMenuName } from "@/utils/menuI18n";

  const store = useStore();
  const { t } = useI18n();
  const router = useRouter();

  // Template refs
  const menuSectionRef = ref<HTMLElement | null>(null);
  const activeMenuRef = ref<HTMLElement | null>(null);

  // store가 제대로 초기화되지 않았을 때의 에러 처리
  if (!store || !store.getters) {
    console.warn("Store가 초기화되지 않았습니다. 홈으로 이동합니다.");
    // 홈으로 이동
    window.location.href = "/";
  }

  // 사이트 리스트 가져오기
  const siteList = computed(() => {
    try {
      const sites = localStorage.getItem("siteList");
      if (sites) {
        return JSON.parse(sites);
      }
      return [];
    } catch (error) {
      console.error("사이트 리스트 파싱 오류:", error);
      return [];
    }
  });

  // 상위 메뉴 변경 시 서브메뉴 선택 초기화
  watch(
    () => {
      if (!store || !store.getters) {
        return null;
      }
      return store.getters.currentSelectedMenu;
    },
    (newMenu, oldMenu) => {
      if (!store || !store.commit) {
        return;
      }

      if (newMenu !== oldMenu) {
        // 상위 메뉴가 변경되면 서브메뉴 선택 초기화
        store.commit("currentSelectedSubMenu", null);

        // 현재 경로에 맞는 서브메뉴 자동 선택
        const currentPath = window.location.pathname;
        const menuData = store.getters.menu || [];

        // 현재 경로와 일치하는 서브메뉴 찾기
        const matchingSubMenu = menuData.find((menu: any) => {
          const menuAddress = menu.menuAddress || menu.menuUrl || menu.menuPath || "";
          return (
            menuAddress === currentPath &&
            (menu.menuLvl === 2 || menu.menuLevel === 2) &&
            (menu.upperMenuId === newMenu || menu.parentMenuId === newMenu || menu.parentId === newMenu)
          );
        });

        if (matchingSubMenu) {
          store.commit("currentSelectedSubMenu", matchingSubMenu.menuId || matchingSubMenu.id);
        } else {
          const subMenus = menuData.filter((menu: any) => {
            const parentId = menu.upperMenuId || menu.parentMenuId || menu.parentId;
            return (menu.menuLvl === 2 || menu.menuLevel === 2) && String(parentId) === String(newMenu);
          });

          if (subMenus.length > 0) {
            const firstSubMenu = subMenus[0];
            store.commit("currentSelectedSubMenu", firstSubMenu.menuId || firstSubMenu.id);
          }
        }
      }
    }
  );

  // 현재 선택된 상단 메뉴에 따른 LNB 메뉴 구조 (computed로 변경)
  const currentMenuName = computed(() => {
    // store가 제대로 초기화되지 않았을 때 기본값 반환
    if (!store || !store.getters) {
      return "홈";
    }

    const currentMenu = store.getters.currentSelectedMenu;
    const menuData = store.getters.menu || [];

    // 현재 선택된 메뉴 찾기 (여러 방법으로 시도)
    let selectedMenu = null;

    // 방법 1: menuId로 찾기
    selectedMenu = menuData.find((menu: any) => menu.menuId === currentMenu);

    // 방법 2: id로 찾기
    if (!selectedMenu) {
      selectedMenu = menuData.find((menu: any) => menu.id === currentMenu);
    }

    // 방법 3: 문자열 변환 후 비교
    if (!selectedMenu) {
      selectedMenu = menuData.find(
        (menu: any) => String(menu.menuId) === String(currentMenu) || String(menu.id) === String(currentMenu)
      );
    }

    // 기본값 제거 - 실제 메뉴가 없으면 빈 문자열 반환
    if (!selectedMenu) {
      return "";
    }

    // 동적 번역 적용
    return getTranslatedMenuName(selectedMenu);
  });

  // 현재 메뉴에 따른 아이콘 (computed로 변경)
  const currentMenuIcon = computed(() => {
    // store가 제대로 초기화되지 않았을 때 기본값 반환
    if (!store || !store.getters) {
      return "/images/icon-home.svg";
    }

    const currentMenu = store.getters.currentSelectedMenu;
    const menuData = store.getters.menu || [];

    // 현재 선택된 메뉴 찾기 (여러 방법으로 시도)
    let selectedMenu = null;

    // 방법 1: menuId로 찾기
    selectedMenu = menuData.find((menu: any) => menu.menuId === currentMenu);

    // 방법 2: id로 찾기
    if (!selectedMenu) {
      selectedMenu = menuData.find((menu: any) => menu.id === currentMenu);
    }

    // 방법 3: 문자열 변환 후 비교
    if (!selectedMenu) {
      selectedMenu = menuData.find(
        (menu: any) => String(menu.menuId) === String(currentMenu) || String(menu.id) === String(currentMenu)
      );
    }

    if (!selectedMenu) {
      return "/images/icon-default.svg";
    }

    // 메뉴 타입에 따른 기본 아이콘
    switch (selectedMenu.menuAddress) {
      case "/home":
        return "/images/icon-home.svg";
      case "/monitoring":
        return "/images/icon-monitor.svg";
      case "/robot-control":
        return "/images/icon-robot.svg";
      case "/report":
        return "/images/icon-report.svg";
      case "/common":
        return "/images/icon-management.svg";
      case "/service-management":
        return "/images/icon-service.svg";
      case "/cafe":
        return "/images/icon-cafe.svg";
      default:
        return "/images/icon-default.svg";
    }
  });

  // 현재 메뉴에 따른 LNB 메뉴 목록 (computed로 변경)
  const currentLnbMenus = computed(() => {
    // store가 제대로 초기화되지 않았을 때 기본 메뉴 반환
    if (!store || !store.getters) {
      return [
        { id: "home", name: "홈", path: "/", menuAddress: "/" },
        { id: "dashboard", name: "대시보드", path: "/dashboard", menuAddress: "/dashboard" },
      ];
    }

    const currentMenu = store.getters.currentSelectedMenu;
    const menuData = store.getters.menu || [];

    // 현재 선택된 메뉴의 하위 메뉴들 찾기 (여러 방법으로 시도)
    let subMenus = [];

    // 방법 1: upperMenuId로 찾기
    subMenus = menuData.filter((menu: any) => menu.upperMenuId === currentMenu);

    // 방법 2: parentMenuId로 찾기
    if (subMenus.length === 0) {
      subMenus = menuData.filter((menu: any) => menu.parentMenuId === currentMenu);
    }

    // 방법 3: parentId로 찾기
    if (subMenus.length === 0) {
      subMenus = menuData.filter((menu: any) => menu.parentId === currentMenu);
    }

    // 방법 4: 문자열 변환 후 비교 (타입 통일)
    if (subMenus.length === 0) {
      subMenus = menuData.filter((menu: any) => {
        const parentId = menu.upperMenuId || menu.parentMenuId || menu.parentId;
        return String(parentId) === String(currentMenu);
      });
    }

    // 방법 5: menuLvl 2인 메뉴들 중에서 상위 메뉴가 currentMenu인 것 찾기
    if (subMenus.length === 0) {
      const level2Menus = menuData.filter(
        (menu: any) => menu.menuLvl === 2 || menu.menuLvl === "2" || menu.level === 2 || menu.level === "2"
      );

      // 레벨2 메뉴들 중에서 상위 메뉴가 currentMenu인 것 찾기 (문자열 변환 후 비교)
      subMenus = level2Menus.filter((menu: any) => {
        const parentId = menu.upperMenuId || menu.parentMenuId || menu.parentId;
        return String(parentId) === String(currentMenu);
      });
    }

    // 하위 메뉴가 있으면 사용, 없으면 빈 배열 반환 (기본 메뉴 제거)
    if (subMenus.length > 0) {
      return subMenus.map((menu: any) => ({
        id: menu.menuId || menu.id,
        name: menu.menuName || menu.menuNm,
        menuName: menu.menuName,
        menuNm: menu.menuNm,
        path: menu.menuAddress || menu.path,
        menuAddress: menu.menuAddress || menu.path,
      }));
    }

    // 기본 메뉴 제거 - 빈 배열 반환
    return [];
  });

  const selectMenu = (menu: any) => {
    // store가 제대로 초기화되지 않았을 때 기본 동작
    if (!store || !store.commit) {
      console.warn("Store가 초기화되지 않았습니다. 기본 라우팅만 수행합니다.");
      const routePath = menu.path || menu.menuAddress;
      if (routePath && routePath !== window.location.pathname) {
        try {
          const fullPath = routePath.startsWith("/") ? routePath : `/${routePath}`;
          router.push(fullPath);
        } catch (error) {
          console.error("Routing error:", error);
          router.push("/");
        }
      }
      return;
    }

    // Save submenu selection to store
    store.commit("currentSelectedSubMenu", menu.id);

    // Handle routing - use router.push instead of window.location.href
    const routePath = menu.path || menu.menuAddress;
    if (routePath && routePath !== window.location.pathname) {
      try {
        // Convert relative path to absolute path
        const fullPath = routePath.startsWith("/") ? routePath : `/${routePath}`;
        router.push(fullPath);
      } catch (error) {
        console.error("Routing error:", error);
      }
    }
  };

  // Check if the current submenu is selected
  const isSelectedSubMenu = (menuId: string) => {
    if (!store || !store.getters) {
      return false;
    }

    const currentSubMenu = store.getters.currentSelectedSubMenu;
    const currentSelectedMenu = store.getters.currentSelectedMenu;

    // If there's a selected submenu, check for match
    if (currentSubMenu) {
      const isSelected = String(currentSubMenu) === String(menuId);
      return isSelected;
    }

    // If no submenu is selected but top menu is active,
    // then the first submenu will be auto active
    if (currentSelectedMenu && currentLnbMenus.value.length > 0) {
      const firstSubMenuId = currentLnbMenus.value[0].id;
      const isFirstSubMenu = String(firstSubMenuId) === String(menuId);

      // If this is the first submenu, automatically set it as active
      if (isFirstSubMenu) {
        store.commit("currentSelectedSubMenu", firstSubMenuId);
        return true;
      }
    }

    return false;
  };

  // LNB 접기/펼치기 상태 토글
  const toggleLnbCollapsed = () => {
    if (!store || !store.commit) {
      console.warn("Store가 초기화되지 않았습니다. 기본 동작만 수행합니다.");
      return;
    }
    store.commit("toggleLnbCollapsed");
  };

  // LNB 접힌 상태 확인
  const isLnbCollapsed = computed(() => {
    if (!store || !store.getters) {
      return false;
    }
    return store.getters.isLnbCollapsed;
  });

  // 사용자 정보 (computed로 추가)
  const userName = computed(() => {
    try {
      const loginSession = localStorage.getItem("login-session");
      if (loginSession) {
        const sessionData = JSON.parse(loginSession);
        return sessionData.userName || "사용자";
      }
    } catch (error) {
      console.error("localStorage 파싱 오류:", error);
    }
    return "사용자";
  });

  // 선택된 사이트 이름 (computed로 추가)
  const selectedSiteName = computed(() => {
    try {
      const selectedSite = localStorage.getItem("selectedSiteName");
      if (selectedSite) {
        return selectedSite;
      }

      // selectedSiteName이 없으면 selectedSite 객체에서 추출
      const selectedSiteObj = localStorage.getItem("selectedSite");
      if (selectedSiteObj) {
        const siteData = JSON.parse(selectedSiteObj);
        return siteData.siteName || "";
      }
    } catch (error) {
      console.error("사이트 정보 파싱 오류:", error);
    }
    return "";
  });

  const userRole = computed(() => {
    try {
      const loginSession = localStorage.getItem("login-session");
      if (loginSession) {
        const sessionData = JSON.parse(loginSession);
        const roleId = sessionData.roleId;

        return roleId;
      }
    } catch (error) {
      console.error("localStorage 파싱 오류:", error);
    }
    return "사용자";
  });

  // 언어 설정 (computed로 추가)
  const locale = computed({
    get: () => {
      if (!store || !store.state) {
        return "ko";
      }
      return store.state.locale || "ko";
    },
    set: async lang => {
      if (!store || !store.commit) {
        console.warn("Store가 초기화되지 않았습니다. 언어 설정을 변경할 수 없습니다.");
        return;
      }
      await store.commit("locale", lang);
    },
  });

  // 언어 변경 감지 및 페이지 새로고침
  watch(locale, newLocale => {
    if (newLocale) {
      // 언어 변경 후 페이지 새로고침 (i18n 적용을 위해)
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  });

  // 언어 변경 처리
  const changeLanguage = async event => {
    const newLocale = event.target.value;
    try {
      if (!store || !store.commit) {
        console.warn("Store가 초기화되지 않았습니다. 언어 설정을 변경할 수 없습니다.");
        return;
      }

      await store.commit("locale", newLocale);
      // 언어 변경 후 페이지 새로고침 (i18n 적용을 위해)
      window.location.reload();
    } catch (error) {
      console.error("언어 변경 중 오류 발생:", error);
    }
  };

  // 사이트 변경 클릭 핸들러
  const handleSiteClick = async () => {
    if (siteList.value.length <= 1) {
      return; // 사이트가 1개 이하면 변경 불가
    }

    const confirmed = await popupQuestion(`${t("site.change.confirm")}\n\n${t("site.change.description")}`);

    if (confirmed) {
      // 사이트 변경 시 기존 사이트 정보 초기화
      localStorage.removeItem("selectedSite");
      localStorage.removeItem("selectedSiteName");
      localStorage.removeItem("selectedSiteId");

      // 확인 버튼을 눌렀을 때만 사이트 변경 페이지로 이동
      router.push("/select-site");
    }
  };

  // 로그아웃 처리
  const handleLogoutClick = async () => {
    try {
      // 로그아웃 확인 팝업
      const confirmed = await popupQuestion(t("logout.confirm"));

      if (!confirmed) {
        return; // 사용자가 취소한 경우
      }

      if (!store || !store.dispatch) {
        console.warn("Store가 초기화되지 않았습니다. 로그인 페이지로 이동합니다.");
        // Clear site information
        clearSiteInfo();
        // Use router instead of window.location.href
        await router.replace("/login");
        return;
      }

      // 통일된 로그아웃 핸들러 사용
      await handleLogout();
    } catch (error) {
      console.error("로그아웃 중 오류 발생:", error);
      // 에러가 발생해도 사이트 정보 초기화 후 로그인 페이지로 이동
      clearSiteInfo();
      await router.replace("/login");
    }
  };

  // Site information initialization function
  const clearSiteInfo = () => {
    try {
      // Delete site-related localStorage data
      localStorage.removeItem("selectedSite");
      localStorage.removeItem("selectedSiteId");
      localStorage.removeItem("selectedSiteName");
    } catch (error) {
      console.error("Error occurred while clearing site information:", error);
    }
  };

  // Function to scroll active item into view
  const scrollActiveItemIntoView = async () => {
    await nextTick();

    if (!menuSectionRef.value) return;

    const activeElement = menuSectionRef.value.querySelector(".lnb-menu-item.active");
    if (!activeElement) return;

    const container = menuSectionRef.value;
    const containerHeight = container.clientHeight;
    const containerScrollTop = container.scrollTop;

    const elementTop = (activeElement as HTMLElement).offsetTop;
    const elementHeight = (activeElement as HTMLElement).offsetHeight;
    const elementBottom = elementTop + elementHeight;

    const viewportTop = containerScrollTop;
    const viewportBottom = containerScrollTop + containerHeight;

    // Check if element is fully visible
    const isFullyVisible = elementTop >= viewportTop && elementBottom <= viewportBottom;

    if (!isFullyVisible) {
      // Calculate optimal scroll position to center the active item
      const optimalScrollTop = elementTop - containerHeight / 2 + elementHeight / 2;

      container.scrollTo({
        top: Math.max(0, optimalScrollTop),
        behavior: "smooth",
      });
    }
  };

  // Watch for changes in current submenu selection to scroll active item into view
  watch(
    () => {
      if (!store || !store.getters) return null;
      return store.getters.currentSelectedSubMenu;
    },
    () => {
      scrollActiveItemIntoView();
    }
  );

  // Watch for changes in menu data to scroll active item into view
  watch(
    () => currentLnbMenus.value,
    () => {
      scrollActiveItemIntoView();
    },
    { flush: "post" }
  );

  // Watch for LNB collapse state changes
  watch(
    () => isLnbCollapsed.value,
    newCollapsed => {
      if (!newCollapsed) {
        // When expanding, scroll active item into view
        setTimeout(scrollActiveItemIntoView, 100);
      }
    }
  );
</script>

<style scoped>
  /* left-lnb를 flexbox로 만들어 언어 섹션을 아래쪽에 고정 */
  .left-lnb {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  /* 언어 선택 섹션 - 레벨2 메뉴 밑에 위치 */
  .lnb-language-section {
    padding: var(--spacing-xs) var(--spacing-xs);
    margin: calc(-1 * var(--spacing-lg)) 0;
    margin-top: auto; /* 이제 제대로 작동! flexbox 안에서 아래쪽으로 밀어냄 */
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    background-color: rgba(255, 255, 255, 0.02);
  }

  /* 기존 lang-header 클래스 스타일과 동일하게 적용 */
  .lnb-language-section .lang-header {
    background: url(/images/icon_language.svg) 5px 3px no-repeat;
    background-size: 16px;
    padding-left: var(--spacing-lg);
    width: auto;
    display: inline-block;
  }

  .lnb-language-section .lang-header select {
    color: var(--gray-500);
    font-size: var(--body-s-font-size);
    background: transparent;
    border: none;
    outline: none;
    padding: var(--spacing-xs) var(--spacing-sm);
    cursor: pointer;
    width: auto;
    min-width: 60px;
    border-radius: var(--radius-sm);
  }

  .lnb-language-section .lang-header select:hover {
    color: var(--gray-700);
    background-color: rgba(255, 255, 255, 0.05);
  }

  .lnb-language-section .lang-header select:focus {
    background-color: rgba(255, 255, 255, 0.1);
  }

  /* 메뉴 섹션에 스크롤 기능 추가 */
  .lnb-menu-section {
    flex: 1;
    overflow-y: auto;
    scrollbar-width: thin; /* Firefox */
    scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1); /* Firefox - thumb track */
    max-height: calc(100vh - 300px);
    scroll-behavior: smooth; /* Smooth scrolling */
    padding-right: 4px;
  }

  /* WebKit 기반 브라우저용 스크롤바 스타일 (Chrome, Safari, Edge) */
  .lnb-menu-section::-webkit-scrollbar {
    width: 6px;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
  }

  .lnb-menu-section::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
  }

  .lnb-menu-section::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    min-height: 20px;
  }

  .lnb-menu-section::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
    border-radius: 8px;
  }

  /* Blink 기반 브라우저 (Chrome 등) 추가 지원 */
  .lnb-menu-section::-webkit-scrollbar-corner {
    background: rgba(255, 255, 255, 0.1);
  }

  /* 레벨2 메뉴 텍스트를 더 왼쪽으로 이동 */
  .lnb-menu-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .lnb-menu-item {
    padding: var(--spacing-md) var(--spacing-md);
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: var(--radius-2xl);
  }

  .lnb-menu-item:hover {
    background: rgba(15, 19, 18, 0.1);
  }

  .lnb-menu-item.active {
    background: var(--gray-900);
    border-radius: var(--radius-2xl);
  }

  .lnb-menu-item.active .lnb-menu-text {
    color: var(--white);
    font-weight: 700;
  }

  .lnb-menu-text {
    color: var(--gray-600);
    font-family: "Pretendard", sans-serif;
    font-weight: 500;
    font-size: var(--body-m-font-size);
    line-height: 1.375;
    padding-left: 0; /* 왼쪽 패딩 제거 */
    text-align: left; /* 텍스트 왼쪽 정렬 */
    display: block; /* 블록 레벨 요소로 변경 */
    width: 100%; /* 전체 너비 사용 */
    margin-left: calc(-1 * var(--spacing-sm)); /* 추가로 왼쪽으로 이동 */
  }

  /* 접기/펼치기 버튼 호버 효과 */
  .lnb-collapse-btn:hover {
    transform: scale(1.05);
    box-shadow: 0px 4px 8px 0px rgba(36, 42, 43, 0.12);
  }

  /* 접힌 상태일 때 메뉴 아이템 숨김 */
  .left-lnb.collapsed .lnb-menu-section,
  .left-lnb.collapsed .lnb-language-section {
    display: none;
  }

  /* 접힌 상태일 때 사용자 정보 숨김 */
  .left-lnb.collapsed .lnb-user-details {
    display: none;
  }

  /* 접힌 상태일 때 로그아웃 버튼 숨김 */
  .left-lnb.collapsed .lnb-logout-btn {
    display: none;
  }

  /* 접힌 상태일 때 구분선 */
  .lnb-collapsed-divider {
    width: 32px;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
    margin: 20px auto;
  }

  /* 접힌 상태일 때 사용자 섹션 중앙 정렬 */
  .left-lnb.collapsed .lnb-user-section {
    justify-content: center;
    align-items: center;
    margin-top: auto;
  }

  /* 접힌 상태일 때 사용자 아바타 중앙 정렬 */
  .left-lnb.collapsed .lnb-user-avatar {
    margin: 0 auto;
  }

  /* 사이트 이름 클릭 가능한 스타일 */
  .lnb-user-location.clickable {
    cursor: pointer;
    color: var(--primary-500);
    text-decoration: underline;
  }

  .lnb-user-location.clickable:hover {
    color: var(--primary-600);
  }

  .lnb-user-location.clickable:focus {
    outline: none;
  }

  .site-change-hint {
    font-size: var(--body-s-font-size);
    color: var(--gray-500);
    margin-left: var(--spacing-xs);
  }
</style>
